import { axiosInstance, platformAxiosInstance, queryKeys, sdkAxiosInstance } from './instances'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { appConfig } from '~/constants/appConfig'
import { useAuthStore } from '~/stores/authStore'
import { useTempStore } from '~/stores/tempStore'
import { OperatingSystem } from '~/types/common/device-info'
import { AuthUser } from '~/types/model/user'
import {
  BindAccountRequest,
  EditProfileRequest,
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  VerifyEmailCodeRequest,
  VerifyResetPasswordRequest,
} from '~/types/request/auth'
import {
  BindAccountResponse,
  EditProfileResponse,
  GetMeResponse,
  LoginResponse,
  PlatformLoginResponse,
  RegisterResponse,
  ResetPasswordResponse,
  VerifyEmailCodeResponse,
  VerifyResetPasswordResponse,
} from '~/types/response/auth'
import { BaseResponse } from '~/types/response/base'
import { Encode } from '~/utils/common'
import getDeviceInfo from '~/utils/get-device-info'

export const authService = {
  async guestLogin(): Promise<BaseResponse<AuthUser>> {
    // Generate or get device ID
    let deviceId = 'unknown-device'
    if (getDeviceInfo().os === OperatingSystem.ANDROID) {
      deviceId = 'unknown-device'
    }

    // Get bundle ID from app config
    const bundleId = appConfig.bundleId

    // Determine distribution platform
    const distributionPlatform =
      getDeviceInfo().os === OperatingSystem.IOS ? 'appstore' : 'playstore'

    // Get device language and region
    const locale = navigator.language
    const language = locale.split('-')[0] || 'en'
    const country = locale.split('-')[1] || 'US'

    // Get currency from locale or default to USD
    const currency = 'USD'

    // Debug logging
    console.log('Guest login device info:', {
      deviceId,
      bundleId,
      distributionPlatform,
      language,
      country,
      currency,
      locale: `${language}-${country}`,
    })

    const response = await axiosInstance.post<BaseResponse<AuthUser>>(`/v1/auth/guest/login`, {
      device_id: deviceId,
      bundle_id: bundleId,
      distribution_platform: distributionPlatform,
      language,
      country,
      currency,
    })
    return response.data
  },

  async bindAccount(request: BindAccountRequest): Promise<BindAccountResponse> {
    const response = await sdkAxiosInstance.post<BindAccountResponse>(`/v1/auth/bind`, {
      guest_token: request.guest_token,
    })
    return response.data
  },

  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await sdkAxiosInstance.post<LoginResponse>(`/v1/auth/email/login`, {
      cred_key: request.cred_key,
      cred_secret: request.cred_secret,
      no_bind_guest: request.no_bind_guest,
    })

    const platformResponse = await platformAxiosInstance.post<PlatformLoginResponse>(
      `/account/v1/public/auth/v2/login`,
      {
        email: request.cred_key,
        password: Encode(appConfig.platformSalt, request.cred_secret),
      },
    )

    return {
      ...response.data,
      data: {
        ...response.data.data,
        platform_access_token: platformResponse.data.data.token,
      },
    }
  },

  async register(request: RegisterRequest): Promise<RegisterResponse> {
    const response = await sdkAxiosInstance.post<RegisterResponse>(`/v1/auth/email/register`, {
      cred_key: request.cred_key,
      cred_secret: request.cred_secret,
      provider: request.provider,
      no_bind_guest: request.no_bind_guest,
    })
    return response.data
  },

  async verifyEmailCode(request: VerifyEmailCodeRequest): Promise<VerifyEmailCodeResponse> {
    const response = await sdkAxiosInstance.post<VerifyEmailCodeResponse>(
      `/v1/auth/email/verify-registration`,
      {
        id: request.id,
        code: request.code,
        no_bind_guest: request.no_bind_guest,
      },
    )
    return response.data
  },

  async resetPassword(request: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await sdkAxiosInstance.post<ResetPasswordResponse>(
      `/v1/auth/email/reset-password`,
      {
        cred_key: request.cred_key,
        no_bind_guest: request.no_bind_guest,
      },
    )
    return response.data
  },

  async verifyResetPassword(
    request: VerifyResetPasswordRequest,
  ): Promise<VerifyResetPasswordResponse> {
    const response = await sdkAxiosInstance.post<VerifyResetPasswordResponse>(
      `/v1/auth/email/verify-reset-password`,
      {
        id: request.id,
        code: request.code,
        no_bind_guest: request.no_bind_guest,
        new_password: request.new_password,
      },
    )
    return response.data
  },

  async editProfile(request: EditProfileRequest): Promise<EditProfileResponse> {
    const token = localStorage.getItem('access_token')
    const response = await sdkAxiosInstance.post<EditProfileResponse>(
      `/v1/user/profile`,
      {
        nickname: request.nickname,
        date_of_birth: request.date_of_birth,
        gender: request.gender,
        picture_url: request.picture_url,
        language: request.language,
        country: request.country,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
    return response.data
  },

  async me(): Promise<GetMeResponse> {
    const token = localStorage.getItem('access_token')
    const platformToken = localStorage.getItem('platform_access_token')
    const response = await sdkAxiosInstance.get<GetMeResponse>(`/v1/user/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    const platformResponse = await platformAxiosInstance.get<GetMeResponse>(
      `/account/v1/private/users/me`,
      {
        headers: {
          Authorization: `Bearer ${platformToken}`,
        },
      },
    )

    return {
      ...response.data,
      data: {
        ...response.data.data,
        vipProgress: platformResponse.data.data.vipProgress,
      },
    }
  },
}

export const useInitGuestAccount = () => {
  const { setAuthUser } = useAuthStore()
  return useMutation<BaseResponse<AuthUser>, Error, void, unknown>({
    mutationFn: () => authService.guestLogin(),
    onSuccess: async (data) => {
      try {
        setAuthUser(data.data.user, data.data.credential)
        const token = data.data.access_token
        localStorage.setItem('access_token', token)
        console.log('Guest account initialized successfully')
      } catch (error) {
        console.error('Error initializing guest account:', error)
      }
    },
    onError: (error) => {
      console.error('Failed to initialize guest account:', error)
    },
  })
}

export const useBindAccount = ({
  onSuccess,
}: {
  onSuccess: (data: BindAccountResponse) => void
}) => {
  return useMutation<BindAccountResponse, Error, BindAccountRequest, unknown>({
    mutationFn: (data: BindAccountRequest) => authService.bindAccount(data),
    onSuccess: (data) => {
      onSuccess(data)
    },
    onError: (error) => {
      console.error('Failed to bind account:', error)
    },
  })
}

export const useLogin = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void
  onError: (error: Error) => void
}) => {
  const { setAuthUser } = useAuthStore()
  const queryClient = useQueryClient()
  return useMutation<LoginResponse, Error, LoginRequest, unknown>({
    mutationFn: (data: LoginRequest) => authService.login(data),
    onSuccess: (data) => {
      const token = data.data.access_token
      const platformToken = data.data.platform_access_token
      localStorage.setItem('access_token', token)
      localStorage.setItem('platform_access_token', platformToken)
      setAuthUser(data.data.user, data.data.credential)
      queryClient.clear()
      onSuccess()
    },
    onError: (error: Error) => {
      onError(error)
    },
  })
}

export const useRegister = ({
  onSuccess,
  onError,
}: {
  onSuccess: (data: RegisterResponse) => void
  onError: (error: Error) => void
}) => {
  return useMutation<RegisterResponse, Error, RegisterRequest, unknown>({
    mutationFn: (data: RegisterRequest) => authService.register(data),
    onSuccess: (data) => {
      onSuccess(data)
    },
    onError: (error) => {
      onError(error)
    },
  })
}

export const useVerifyEmailCode = ({
  onSuccess,
  onError,
}: {
  onSuccess: (data: VerifyEmailCodeResponse) => void
  onError: (error: Error) => void
}) => {
  return useMutation<VerifyEmailCodeResponse, Error, VerifyEmailCodeRequest, unknown>({
    mutationFn: (data: VerifyEmailCodeRequest) => authService.verifyEmailCode(data),
    onSuccess: (data) => {
      onSuccess(data)
    },
    onError: (error) => {
      onError(error)
    },
  })
}

export const useResetPassword = ({
  onSuccess,
  onError,
}: {
  onSuccess: (data: ResetPasswordResponse) => void
  onError: (error: Error) => void
}) => {
  return useMutation<ResetPasswordResponse, Error, ResetPasswordRequest, unknown>({
    mutationFn: (data: ResetPasswordRequest) => authService.resetPassword(data),
    onSuccess: (data) => {
      onSuccess(data)
    },
    onError: (error) => {
      onError(error)
    },
  })
}

export const useVerifyResetPassword = ({
  onSuccess,
  onError,
}: {
  onSuccess: (data: VerifyResetPasswordResponse) => void
  onError: (error: Error) => void
}) => {
  return useMutation<VerifyResetPasswordResponse, Error, VerifyResetPasswordRequest, unknown>({
    mutationFn: (data: VerifyResetPasswordRequest) => authService.verifyResetPassword(data),
    onSuccess: (data) => {
      onSuccess(data)
    },
    onError: (error) => {
      onError(error)
    },
  })
}

export const useEditProfile = ({
  onSuccess,
  onError,
}: {
  onSuccess: (data: EditProfileResponse) => void
  onError: (error: Error) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation<EditProfileResponse, Error, EditProfileRequest, unknown>({
    mutationFn: (data: EditProfileRequest) => authService.editProfile(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
      onSuccess(data)
    },
    onError: (error) => {
      onError(error)
    },
  })
}

export const useGetMe = (accessToken: string | null) => {
  return useQuery<GetMeResponse, Error>({
    queryKey: queryKeys.auth.me(),
    queryFn: () => authService.me(),
    staleTime: 0,
    enabled: !!accessToken, // Only run when accessToken is available
  })
}

export const useLogout = () => {
  const { reset } = useAuthStore()
  const { reset: resetTemp } = useTempStore()
  const queryClient = useQueryClient()

  const logout = async () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('platform_access_token')
    reset()
    resetTemp()
    queryClient.clear()
  }

  return { logout }
}
